import _ from 'lodash';
import React, { useEffect } from 'react';
import useToggle from 'react-use-v13/lib/useToggle';

// @ts-ignore
import cx from 'classnames'; // tslint:disable-line: match-default-export-name
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { remotasksURL } from '@scale/frontend-lib/urls';
// TODO: Use new button
// eslint-disable-next-line import-path/forbidden
import Button from 'design-system/ButtonLegacy';
import { InputCheckbox, Label } from 'design-system/Form';
import { Modal } from 'design-system/Modal';
import { analytics } from '@scale/frontend-lib/analytics/segment-source-scalers';
import {
  BasicLidarCorruptions,
  BasicVideoCorruptions,
  BasicLidarSegCorruptions,
  BasicTextCollectionCorruptions,
  BasicSemsegCorruptions,
  BasicImageCorruptions,
  CorruptionTypes,
  BasicDocumentCorruptions,
} from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import {
  TaskerModalHeader,
  TaskerModalHeaderIcon,
  TaskerModalBody,
  TaskerModalFooter,
} from 'react-scale/components/scalers/tasks/common/TaskerModal';

const corruptionTypeStrings: { [k in CorruptionTypes]?: string } = {
  // LidarAnnotation corruption feedback.
  [BasicLidarCorruptions.BasicLabel]: 'Labels / Attributes',
  [BasicLidarCorruptions.BasicAttribute]: 'Labels / Attributes',
  [BasicLidarCorruptions.BasicRecall]: 'Missing / Unnecessary Cuboids',
  [BasicLidarCorruptions.BasicRecallPedestrian]: 'Missing Pedestrian Cuboids', // pretty jank -- will remove Q1/Q2.
  [BasicLidarCorruptions.BasicZ]: "Cuboids Don't Touch the Ground",
  [BasicLidarCorruptions.BasicPosition]: 'Position, Cuboids are Not Centered',
  [BasicLidarCorruptions.BasicSize]: 'Sizing, Missing Points',
  [BasicLidarCorruptions.BasicMissingKeyframe]: 'Position / Sizing at Each Frame',
  [BasicLidarCorruptions.BasicParkedAttribute]: 'Stationary Vehicle Not Parked',

  // Video Corruption Feedback.
  [BasicVideoCorruptions.Height]: 'Box Sizing',
  [BasicVideoCorruptions.SingleFrameSize]: 'Box Sizing',
  [BasicVideoCorruptions.GroupedAttribute]: 'Labels / Attributes',
  [BasicVideoCorruptions.Attribute]: 'Labels / Attributes',
  [BasicVideoCorruptions.GlobalPathAttributeText]: 'Annotation Path Attribute',

  // PCS corruption feedback. Please keep the feedback vague.
  [BasicLidarSegCorruptions.Label]: 'Incorrect Instance Labels',
  [BasicLidarSegCorruptions.Instance]: 'Instances Must be Unique',

  // Text Collection feedback.
  [BasicTextCollectionCorruptions.RemoveCategory]: 'Missing Categories',
  [BasicTextCollectionCorruptions.RemoveItem]: 'Missing Items',
  [BasicTextCollectionCorruptions.RemovePrice]: 'Incorrect Prices',
  [BasicTextCollectionCorruptions.RemoveExtra]: 'Missing Extras',
  [BasicTextCollectionCorruptions.RemoveOption]: 'Missing Options',
  [BasicTextCollectionCorruptions.MutatePrice]: 'Incorrect Prices',
  [BasicTextCollectionCorruptions.RemoveModel]: 'Missing Model',
  [BasicTextCollectionCorruptions.MutateProductDescription]: 'Incorrect Description',
  [BasicTextCollectionCorruptions.MutateUPC]: 'Incorrect UPC',

  // Semseg Corruption feedback.
  [BasicSemsegCorruptions.Label]: 'Incorrect Labels',

  // Image corruption feedback.
  [BasicImageCorruptions.Missing]: 'Missing Annotation',
  [BasicImageCorruptions.Extraneous]: 'Extraneous Annotation',

  // Document corruption feedback.
  [BasicDocumentCorruptions.Typo]: 'Incorrect Labels',
};

const feedbackCenterUrl = remotasksURL(`/feedback`);
const taskLogUrl = remotasksURL(`/activity`);

interface ICorruptionWarningModalProps {
  corruptionAccuracy?: number;
  improvedAccuracyCorruptionTypes?: string[];
  lowAccuracyCorruptionTypes?: string[];
  taskType: string;
}

export default function CorruptionWarningModal({
  corruptionAccuracy = -1,
  improvedAccuracyCorruptionTypes = [],
  lowAccuracyCorruptionTypes = [],
  taskType,
}: ICorruptionWarningModalProps) {
  const [confirmed, toggleConfirmed] = useToggle(false);
  const [isOpen, toggleIsOpen] = useToggle(true);
  const hasQualityIssues = lowAccuracyCorruptionTypes.length > 0;
  return (
    <Modal contentLabel="CorruptionWarning" maxWidth="500px" isOpen={isOpen}>
      <TaskerModalHeader>
        <TaskerModalHeaderIcon
          icon={hasQualityIssues ? 'lightbulb-exclamation' : 'badge-check'}
          color={hasQualityIssues ? '#000000' : '#19B629'}
        />
        {hasQualityIssues
          ? 'Your last review had quality issues'
          : 'Your last review had high quality'}
      </TaskerModalHeader>
      <TaskerModalBody>
        <CorruptionSummary
          corruptionAccuracy={corruptionAccuracy}
          improvedAccuracyCorruptionTypes={improvedAccuracyCorruptionTypes}
          lowAccuracyCorruptionTypes={lowAccuracyCorruptionTypes}
          taskType={taskType}
        />
      </TaskerModalBody>
      <TaskerModalFooter>
        <div className="checkbox">
          <Label style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <InputCheckbox checked={confirmed} onChange={toggleConfirmed} />
            <span className="guidelines">I agree to the reviewing guidelines</span>
          </Label>
        </div>
        <div className="submit">
          <Button
            type="primary"
            disabled={hasQualityIssues && !confirmed}
            onClick={toggleIsOpen}
            style={{ width: '100%' }}
          >
            Begin Task
          </Button>
        </div>
      </TaskerModalFooter>
      <style jsx>{`
        .checkbox {
          flex: 1 0 68%;
          margin-right: 2%;
        }
        .guidelines {
          margin-left: 5px;
          display: inline-block;
        }
        .submit {
          flex: 1 0 30%;
        }
      `}</style>
    </Modal>
  );
}

interface ICorruptionSummaryProps {
  corruptionAccuracy?: ICorruptionWarningModalProps['corruptionAccuracy'];
  improvedAccuracyCorruptionTypes?: ICorruptionWarningModalProps['improvedAccuracyCorruptionTypes'];
  lowAccuracyCorruptionTypes?: ICorruptionWarningModalProps['lowAccuracyCorruptionTypes'];
  postReviewSubmission?: boolean;
  taskType: string;
}

export function CorruptionSummary({
  corruptionAccuracy = -1,
  improvedAccuracyCorruptionTypes = [],
  lowAccuracyCorruptionTypes = [],
  postReviewSubmission = false,
  taskType,
}: ICorruptionSummaryProps) {
  // NOTE: event is sent from this component because it could also be sent from the submit modal
  // (submit modal uses only this component).
  useEffect(() => {
    sendCorruptionsWarningEvent(lowAccuracyCorruptionTypes, improvedAccuracyCorruptionTypes);
  }, [lowAccuracyCorruptionTypes, improvedAccuracyCorruptionTypes]);
  const hasQualityIssues = lowAccuracyCorruptionTypes.length > 0;
  const hasImprovements = improvedAccuracyCorruptionTypes.length > 0;
  const hasCorruptionAccuracy = corruptionAccuracy >= 0;
  const showPayChange = shouldShowPayChange(taskType);
  return (
    <div>
      {!hasQualityIssues && (
        <div className="message">Good job passing our automatic quality checks in this review!</div>
      )}
      {hasQualityIssues && (
        <CorruptionSummaryAccuracyCategories corruptionTypes={lowAccuracyCorruptionTypes} />
      )}
      {hasImprovements && (
        <CorruptionSummaryAccuracyCategories
          corruptionTypes={improvedAccuracyCorruptionTypes}
          isImprovement
        />
      )}
      <CorruptionSummarySupport
        postReviewSubmission={postReviewSubmission}
        hasQualityIssues={hasQualityIssues}
      />
      {postReviewSubmission && hasCorruptionAccuracy && showPayChange && (
        <CorruptionSummaryPayout corruptionAccuracy={corruptionAccuracy} />
      )}
      <style jsx>{`
        .message {
          padding-bottom: 10px;
        }
      `}</style>
    </div>
  );
}

interface ICorruptionSummaryAccuracyCategories {
  corruptionTypes: string[];
  isImprovement?: boolean;
}

function CorruptionSummaryAccuracyCategories({
  corruptionTypes,
  isImprovement = false,
}: ICorruptionSummaryAccuracyCategories) {
  return (
    <CorruptionSummarySection
      title={isImprovement ? 'Improvement' : 'Issues'}
      icon="clipboard-check"
    >
      <React.Fragment>
        {corruptionTypesToStrings(corruptionTypes).map(corruptionLabel => (
          <span
            key={corruptionLabel}
            className={cx('tag', isImprovement ? 'tag--improvement' : 'tag--issue')}
          >
            {corruptionLabel}
          </span>
        ))}
        <style jsx>{`
          .tag {
            border-radius: 8px;
            display: inline-block;
            margin: 8px 8px 8px 0;
            padding: 8px;
            white-space: nowrap;
          }
          .tag--improvement {
            background-color: #d9eeff;
          }
          .tag--issue {
            background-color: #fbd0cf;
          }
        `}</style>
      </React.Fragment>
    </CorruptionSummarySection>
  );
}

interface ICorruptionSummarySupportProps {
  hasQualityIssues: boolean;
  postReviewSubmission: boolean;
}

function CorruptionSummarySupport({
  hasQualityIssues,
  postReviewSubmission,
}: ICorruptionSummarySupportProps) {
  return (
    <CorruptionSummarySection title="Support" icon="lightbulb">
      <React.Fragment>
        {hasQualityIssues && !postReviewSubmission && (
          <div className="support-reminder">
            Remember to pay attention to these quality issues. We will let you know after submitting
            if you are improving!
          </div>
        )}
        {hasQualityIssues ? (
          <div>
            If quality issues continue in future tasks, you will be removed as a Reviewer. We
            recommend reviewing task instructions and courses to improve your skills, visiting the{' '}
            <a href={feedbackCenterUrl}>feedback center</a> and your{' '}
            <a href={taskLogUrl}>task log</a>, and using Slack to ask questions.
          </div>
        ) : (
          <div>
            Keep improving your skills by reviewing task instructions and courses, visiting the{' '}
            <a href={feedbackCenterUrl}>feedback center</a> and your{' '}
            <a href={taskLogUrl}>task log</a>, and using Slack to ask questions.
          </div>
        )}
        <style jsx>{`
          .support-reminder {
            padding-bottom: 10px;
          }
        `}</style>
      </React.Fragment>
    </CorruptionSummarySection>
  );
}

interface ICorruptionSummaryPayoutProps {
  corruptionAccuracy?: ICorruptionWarningModalProps['corruptionAccuracy'];
}

function CorruptionSummaryPayout({ corruptionAccuracy = -1 }: ICorruptionSummaryPayoutProps) {
  if (corruptionAccuracy < 0) {
    return null;
  }
  let payoutExplanation =
    'Your payout will not be immediately affected, but if our manual quality checks reveal that your quality is low, your pay may be reduced and you may be demoted to an attempter.';
  if (corruptionAccuracy === 100) {
    payoutExplanation = 'You will receive a $1 bonus for this review.';
  } else if (corruptionAccuracy < 50) {
    payoutExplanation = 'Your payout for this review will be reduced by 50%.';
  }
  return (
    <CorruptionSummarySection title="Payout" icon="badge-dollar">
      {payoutExplanation}
    </CorruptionSummarySection>
  );
}

interface ICorruptionSummarySectionProps {
  children: React.ReactNode;
  icon: string;
  title: string;
}

function CorruptionSummarySection({ icon, title, children }: ICorruptionSummarySectionProps) {
  return (
    <div className="section">
      <div className="section-title">
        <FontAwesomeIcon icon={icon as IconProp} />{' '}
        <div className="section-title__title">{title}</div>
      </div>
      <div className="section-children">{children}</div>
      <style jsx>{`
        .section {
          color: #000;
        }
        .section:not(:last-child) {
          padding-bottom: 10px;
        }
        .section-title {
          align-items: center;
          display: flex;
          font-size: 14px;
          font-weight: bold;
          padding-bottom: 2px;
        }
        .section-title__title {
          margin-left: 11px;
        }
        .section-children {
          font-size: 14px;
          padding: 1px;
        }
      `}</style>
    </div>
  );
}

function sendCorruptionsWarningEvent(
  lowAccuracyCorruptionTypes: ICorruptionWarningModalProps['lowAccuracyCorruptionTypes'] = [],
  improvedAccuracyCorruptionTypes: ICorruptionWarningModalProps['improvedAccuracyCorruptionTypes'] = [],
): void {
  const lowAccuracyCorruptionTypesForEvent = lowAccuracyCorruptionTypes.map(corruptionType => ({
    type: corruptionType,
    feedback: 'failed',
  }));
  const improvedAccuracyCorruptionTypesForEvent = improvedAccuracyCorruptionTypes.map(
    corruptionType => ({
      type: corruptionType,
      feedback: 'improved',
    }),
  );
  const corruptions = [
    ...lowAccuracyCorruptionTypesForEvent,
    ...improvedAccuracyCorruptionTypesForEvent,
  ];
  analytics.track('Corruption Warning Modal Loaded', { corruptions });
}

function corruptionTypesToStrings(corruptionTypes: string[]): string[] {
  return _.compact(
    corruptionTypes.map(corruptionType => corruptionTypeStrings[corruptionType as CorruptionTypes]),
  );
}

function shouldShowPayChange(taskType: string): boolean {
  return taskType === TaskType.LidarAnnotation;
}
